import { useLazyQuery } from "@apollo/client";
import debounce from "lodash.debounce";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

import { Separator } from "../ui/separator";
import { SEARCH_QUERY } from "./graphql";
import Header from "./header";
import type { UsersDataType, UsersVariablesType, UserType } from "./types";
import User from "./user";

export type { UsersDataType, UsersVariablesType, UserType } from "./types";

interface UserSearchModal extends React.ComponentProps<typeof Dialog> {
  buttonLabel?: string;
  onSelect?: (user: UserType) => Promise<UserType>;
  onSubmit(users: UserType[]): void;
  readonlyUsers?: string[];
  title: string;
  users?: UserType[];
}

export default function UserSearchModal({
  buttonLabel = "Save",
  onSelect,
  onSubmit,
  readonlyUsers = [],
  title,
  users,
  ...props
}: UserSearchModal) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedUsers, setSelectedUsers] = useState<UserType[]>([]);
  const selectedUserIds = useMemo(
    () => selectedUsers.map((u) => u.id),
    [selectedUsers]
  );

  const { queryContext, isUser } = useAuth();
  const [fetchUsers, { data }] = useLazyQuery<
    UsersDataType,
    UsersVariablesType
  >(SEARCH_QUERY);

  const debouncedSearch = useCallback(debounce(fetchUsers, 500), []);

  const handleSearch = (query: string) =>
    debouncedSearch(queryContext({ query }));

  const handleSubmit = () => onSubmit(selectedUsers);

  const handleToggle = (user: UserType) => {
    if (readonlyUsers.includes(user.id)) {
      return;
    }
    if (selectedUserIds.includes(user.id)) {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    } else if (typeof onSelect === "function") {
      onSelect(user).then((add) => setSelectedUsers([...selectedUsers, add]));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: im an engineer
  useEffect(() => {
    if (isUser) {
      fetchUsers(queryContext());
    }
  }, [isUser]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (users) {
      setSelectedUsers(users);
    }
  }, [users]);

  const usersList = useMemo(
    () => data?.suggestTaggedUsers.objects || [],
    [data]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on new list
  useEffect(() => {
    containerRef.current?.scrollTo(0, 0);
  }, [usersList]);

  return (
    <Dialog {...props}>
      <DialogContent
        className="flex min-h-full flex-col gap-0 overflow-hidden rounded-none bg-twilight-indigo shadow-0-0-8 shadow-black/25 md:min-h-auto md:w-sm md:rounded-[15px]"
        overlayClassName="md:items-center"
      >
        <Header
          buttonLabel={buttonLabel}
          onClose={() => props.onOpenChange?.(false)}
          onSearch={handleSearch}
          onSubmit={handleSubmit}
          onToggle={handleToggle}
          readonlyUsers={readonlyUsers}
          title={title}
          users={selectedUsers}
        />
        <div
          className={cn(
            "relative flex h-full grow-1 flex-col gap-2.5 overflow-y-auto px-5 py-6",
            'after:absolute after:inset-x-0 after:bottom-0 after:z-1 after:hidden after:h-6 after:bg-gradient-0 after:from-20% after:from-twilight-indigo after:to-transparent after:content-[""] md:after:block',
            'before:absolute before:inset-x-0 before:top-0 before:z-1 before:block before:h-6 before:bg-gradient-180 before:from-20% before:from-twilight-indigo before:to-transparent before:content-[""]',
            selectedUsers.length
              ? "max-h-[70vh] md:max-h-[60vh]"
              : "max-h-[80vh] md:max-h-[70vh]"
          )}
          ref={containerRef}
        >
          {usersList.map((user, index) => (
            <Fragment key={user.id}>
              {!!index && <Separator className="bg-white/10" />}
              <User
                isSelected={selectedUserIds.includes(user.id)}
                onToggle={handleToggle}
                user={user}
              />
            </Fragment>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
